import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, MessageCircle, Users, Heart, Hand, AlertTriangle, ChevronRight, Lock, Anchor, EarOff } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import { BackToTopButton, ContentNavFooter } from '../components/NavigationHelpers';
import { Button } from '../components/ui/button';
import { authFetch } from '../lib/authFetch';
import useTheme4A from '../components/useTheme4A';

const SECTIONS = [
  {
    id: 'man-to-man',
    icon: Users,
    color: 'sky',
    title: 'Healthy man-to-man communication',
    body: (
      <>
        <p className="mb-3">
          Many men in early Recovery have spent years using substances to numb their feelings. The common pattern
          is that we either say nothing about what's actually going on inside or we explode about it.
          Neither serves Recovery. The third path: the slower path: is the work.
        </p>
        <p className="mb-3 font-semibold text-zinc-800">Practise saying these out loud to another man:</p>
        <ul className="list-none space-y-2 mb-3">
          <li className="bg-sky-50 border border-sky-200 rounded-md px-3 py-2 text-sky-900">"I'm struggling today and I just need you to listen for ten minutes."</li>
          <li className="bg-sky-50 border border-sky-200 rounded-md px-3 py-2 text-sky-900">"I don't have an answer for this yet: I'm sitting with it."</li>
          <li className="bg-sky-50 border border-sky-200 rounded-md px-3 py-2 text-sky-900">"What you said landed hard. I need a bit of time before I respond."</li>
          <li className="bg-sky-50 border border-sky-200 rounded-md px-3 py-2 text-sky-900">"Thanks for telling me. I'm glad you did."</li>
        </ul>
        <p>
          Naming what's happening inside you: without performing, without armour: is one of the most useful
          skills in long-term Recovery. You don't have to fix anyone's feelings, including your own. Just say
          what's true.
        </p>
      </>
    ),
  },
  {
    id: 'no-13th-step',
    icon: Shield,
    color: 'rose',
    title: 'Don\'t be a 13th-stepper. Ever.',
    body: (
      <>
        <p className="mb-3">
          The 13th Step is when an experienced man in a Recovery community sexually or romantically pursues a
          newcomer woman. Inside Axiom ARP, we are explicit about this: <strong>13th-stepping is harm.</strong>
        </p>
        <p className="mb-3 font-semibold text-zinc-800">If you've been in Recovery longer than the women you're meeting:</p>
        <ul className="list-disc ml-5 space-y-1.5 mb-3">
          <li>Don't DM newcomer women. Don't "check in on her". Don't offer rides. Don't hand her your phone number unprompted.</li>
          <li>Don't comment on her appearance: not even kindly.</li>
          <li>Don't pull her aside after a meeting to talk privately.</li>
          <li>If a newcomer woman wants Recovery support, point her toward another woman with sober time. Every time.</li>
        </ul>
        <p className="mb-3">
          The honest, uncomfortable question to sit with: <em>am I drawn to helping her or am I drawn to her?</em>
          If the answer isn't immediate and certain, step back. Talk to your sponsor about it. That conversation
          itself is recovery work.
        </p>
        <p className="text-[12px] text-zinc-600 italic">
          This isn't about pretending women don't exist. It's about the asymmetry: sober time is read as safety,
          and exploiting that asymmetry, even unintentionally, is a relapse trigger for both of you.
        </p>
      </>
    ),
  },
  {
    id: 'recognising-patterns',
    icon: AlertTriangle,
    color: 'amber',
    title: 'Recognising your own patterns from active addiction',
    body: (
      <>
        <p className="mb-3">
          Most of us spent years deflecting, charming, lying, using sex or anger or money to manage other people.
          These patterns don't evaporate the day we put the substance down. They follow us into Recovery wearing
          new clothes.
        </p>
        <p className="mb-3 font-semibold text-zinc-800">Common patterns to watch in yourself:</p>
        <ul className="list-disc ml-5 space-y-1.5 mb-3">
          <li><strong>Charm-as-control</strong>: making someone laugh so they won't push back on you.</li>
          <li><strong>The white lie reflex</strong>: minor untruths to make the moment easier ("I'm fine", "yeah, I read it").</li>
          <li><strong>Performative service</strong>: being unusually helpful to someone you secretly want approval from.</li>
          <li><strong>Score-keeping</strong>: quietly tallying what others owe you for things you offered freely.</li>
          <li><strong>Anger-as-shortcut</strong>: escalating volume because you don't have the words for the actual feeling.</li>
        </ul>
        <p>
          The work is to <em>notice</em> the pattern arising, name it (out loud or to your sponsor) and choose
          the slower, more honest path. You will not get this right every time. You will get it right more often
          than you used to. That's progress.
        </p>
      </>
    ),
  },
  {
    id: 'attraction',
    icon: Heart,
    color: 'violet',
    title: 'Healthy expressions of attraction',
    body: (
      <>
        <p className="mb-3">
          Attraction itself is not a problem. Acting on attraction toward a vulnerable person or weaponising your
          longer sober time, is.
        </p>
        <p className="mb-3 font-semibold text-zinc-800">What healthy looks like in early Recovery:</p>
        <ul className="list-disc ml-5 space-y-1.5 mb-3">
          <li>You feel attracted to someone. You notice it. You don't act on it for a long time.</li>
          <li>If you do explore a relationship eventually, you do so with someone outside the meeting room: not a newcomer.</li>
          <li>You talk about your dating life with your sponsor and your therapist, not the women you're attracted to.</li>
          <li>You accept "no" the first time, without negotiation, without sulking, without "but why".</li>
          <li>You do not text after midnight. You do not keep tabs on her social media. You do not show up uninvited.</li>
        </ul>
        <p className="text-[12px] text-zinc-600 italic">
          A useful self-test: would this behaviour read as fine to my sponsor and to her sober female sponsor?
          If you'd hesitate to mention it to either of them, don't do it.
        </p>
      </>
    ),
  },
  {
    id: 'accountability',
    icon: Anchor,
    color: 'emerald',
    title: 'Accountability: the man you actually want to be',
    body: (
      <>
        <p className="mb-3">
          Accountability is not punishment. It's the practice of being known: by another sober man: at the level
          of detail where lying becomes pointless. That's where Recovery roots.
        </p>
        <ul className="list-disc ml-5 space-y-1.5 mb-3">
          <li><strong>Pick an accountability partner</strong>: a sober man with at least 12 months of clean time, ideally your sponsor or a step-brother.</li>
          <li><strong>Daily check-in</strong>: short, honest. "Today I felt X, I did Y, I'm watching for Z."</li>
          <li><strong>Promptly clean up wreckage</strong>: when you mess up (and you will), own it the same day, in person, in plain words. No "I just want to say I'm sorry if you felt…": try "I did this, it was wrong, I'm sorry, here's what I'm doing differently."</li>
          <li><strong>Make amends without expecting absolution</strong>: the amends is the gift; the response is not yours to control.</li>
        </ul>
        <p>
          Men who do this consistently for two years become almost unrecognisable to themselves. That's not a
          slogan: it's pattern matching across thousands of recovered men.
        </p>
      </>
    ),
  },
  {
    id: 'when-to-walk-away',
    icon: EarOff,
    color: 'sky',
    title: 'Saying nothing: the underrated skill',
    body: (
      <>
        <p className="mb-3">
          Most relapses for men in early Recovery are downstream of an interaction that escalated. Someone said
          something that hurt your pride, you fired back, the conversation got ugly, you needed a drink to take
          the edge off. Walking away is the move.
        </p>
        <ul className="list-disc ml-5 space-y-1.5 mb-3">
          <li><strong>Acknowledge and exit:</strong> "I'm hearing you. I need to walk away from this conversation right now. We can pick it up later." Then leave.</li>
          <li><strong>You don't owe a comeback.</strong> Silence is not weakness. It's often the most effective response.</li>
          <li><strong>Three breaths before any reply</strong> when you're activated. If you're still activated after three breaths, take three more.</li>
          <li><strong>Texts can wait 4 hours.</strong> Phone calls can wait 24. Almost nothing in Recovery actually requires an instant response.</li>
        </ul>
        <p>
          The man you're becoming has nothing to prove. Practising this gets you there.
        </p>
      </>
    ),
  },
];

const COLOR = {
  rose:    { bg: 'bg-rose-100',    text: 'text-rose-700',    ring: 'ring-rose-200' },
  amber:   { bg: 'bg-amber-100',   text: 'text-amber-700',   ring: 'ring-amber-200' },
  violet:  { bg: 'bg-violet-100',  text: 'text-violet-700',  ring: 'ring-violet-200' },
  emerald: { bg: 'bg-emerald-100', text: 'text-emerald-700', ring: 'ring-emerald-200' },
  sky:     { bg: 'bg-sky-100',     text: 'text-sky-700',     ring: 'ring-sky-200' },
};

function MenOnlyPage() {
  useTheme4A();
  const navigate = useNavigate();
  const [access, setAccess] = useState({ loading: true });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/men-only/access`);
        const d = await r.json();
        if (!cancelled) setAccess({ ...d, loading: false });
      } catch (e) {
        if (!cancelled) setAccess({ allowed: false, reason: 'error', loading: false });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (access.loading) {
    return (
      <div className="min-h-screen bg-zinc-100">
        <PageHeader title="Men Only" />
        <div className="pt-24 px-6 text-center text-sm text-zinc-500">Checking access…</div>
      </div>
    );
  }

  if (!access.allowed) {
    const reasonText = {
      personal_history_required: "Please complete your Personal History first: we use the gender field there to confirm access.",
      male_only: "This area is for men only.",
      error: "Could not verify access. Please try again in a moment.",
    }[access.reason] || "This area is restricted.";
    return (
      <div className="min-h-screen bg-zinc-100">
        <PageHeader title="Men Only" />
        <main className="pt-20 px-4">
          <div className="max-w-md mx-auto">
            <div className="bg-white border border-zinc-200 rounded-xl p-6 text-center" data-testid="men-only-blocked">
              <div className="w-12 h-12 rounded-full bg-sky-100 text-sky-600 flex items-center justify-center mx-auto mb-3">
                <Lock className="w-5 h-5" />
              </div>
              <h2 className="text-base font-bold text-zinc-900 mb-2">Men-only safe space</h2>
              <p className="text-[13px] text-zinc-600 leading-relaxed mb-5">{reasonText}</p>
              {access.reason === 'personal_history_required' ? (
                <Button onClick={() => navigate('/personal-history')} className="w-full bg-zinc-950 text-white hover:bg-zinc-800">
                  Open Personal History
                </Button>
              ) : (
                <Button variant="outline" onClick={() => navigate('/dashboard')} className="w-full">
                  Back to Dashboard
                </Button>
              )}
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-100">
      <PageHeader title="Men Only" />
      <BackToTopButton />

      <main className="pt-20 pb-20 px-4">
        <div className="max-w-md mx-auto space-y-4">

          {/* Hero */}
          <div className="bg-white border border-sky-200 rounded-xl p-5">
            <div className="flex items-start gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-sky-100 text-sky-700 flex items-center justify-center flex-shrink-0">
                <MessageCircle className="w-5 h-5" />
              </div>
              <div>
                <h2 className="text-base font-bold text-zinc-900 leading-tight">A safe space for men in Recovery</h2>
                <p className="text-[12px] text-zinc-600 mt-1 leading-relaxed">
                  The material here is about becoming a man that other sober men (and the women in
                  your fellowship) can trust. It covers honest communication, accountability and
                  the patterns we all have to unlearn after active alcoholism/addiction.
                </p>
              </div>
            </div>
            {access.reason === 'superadmin_review' && (
              <div className="mt-3 px-3 py-2 bg-zinc-100 border border-zinc-200 rounded-md text-[11px] text-zinc-600">
                <AlertTriangle className="w-3.5 h-3.5 inline-block mr-1 text-zinc-500" />
                You are viewing as SuperAdmin (read-only moderation review).
              </div>
            )}
          </div>

          {/* TOC */}
          <div className="bg-white border border-zinc-200 rounded-xl p-3">
            <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 px-2 pt-1 pb-2">In this section</p>
            <div className="space-y-1">
              {SECTIONS.map((s) => {
                const c = COLOR[s.color] || COLOR.sky;
                const Icon = s.icon;
                return (
                  <a
                    key={s.id}
                    href={`#${s.id}`}
                    className="flex items-center gap-3 px-2 py-2 rounded-md hover:bg-zinc-50 transition-colors"
                    data-testid={`men-toc-${s.id}`}
                  >
                    <div className={`w-7 h-7 rounded-md ${c.bg} ${c.text} flex items-center justify-center flex-shrink-0`}>
                      <Icon className="w-3.5 h-3.5" />
                    </div>
                    <span className="text-[12px] text-zinc-800 flex-1">{s.title}</span>
                    <ChevronRight className="w-3.5 h-3.5 text-zinc-300" />
                  </a>
                );
              })}
            </div>
          </div>

          {/* Sections */}
          {SECTIONS.map((s) => {
            const c = COLOR[s.color] || COLOR.sky;
            const Icon = s.icon;
            return (
              <article
                id={s.id}
                key={s.id}
                className="bg-white border border-zinc-200 rounded-xl p-5 scroll-mt-24"
                data-testid={`men-section-${s.id}`}
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className={`w-9 h-9 rounded-lg ${c.bg} ${c.text} flex items-center justify-center flex-shrink-0`}>
                    <Icon className="w-4.5 h-4.5" />
                  </div>
                  <h3 className="text-sm font-bold text-zinc-900">{s.title}</h3>
                </div>
                <div className="text-[13px] text-zinc-700 leading-relaxed">{s.body}</div>
              </article>
            );
          })}

          {/* Closing */}
          <div className="bg-zinc-950 text-zinc-100 rounded-xl p-5">
            <p className="text-[13px] leading-relaxed mb-3">
              The men who go furthest in Recovery are not the loudest or the most charming. They're the ones
              who picked up the phone when they didn't want to, told the truth when nobody was watching and walked
              away from arguments they could have won.
            </p>
            <p className="text-[12px] text-zinc-400 italic">The Axiom ARP team</p>
          </div>

          <ContentNavFooter />
        </div>
      </main>
    </div>
  );
}

export default MenOnlyPage;
